import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Dropdown } from 'react-bootstrap';
import verticalicon from '../../../images/verticalicon.svg';
import ConfirmationPopup from '../../Modals/ConfirmationPopup';
import axios from 'axios';
import { API_BASE_URL, getBearerToken } from '../../../functions';

const fmtDate = (dt) => {
  if (!dt) return '';
  try {
    const date = new Date(dt);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    return String(dt).split(' ')[0];
  }
};

const UmrahTRLoop = ({ packages, onDelete, onClose, onActivate }) => {
  const [confirmation, setConfirmation] = useState({ show: false, target: '', packageId: null });
  const [airlineData, setAirlineData] = useState(null);

  // Fetch airline data
  useEffect(() => {
    axios.get(`${API_BASE_URL}/general`, {
      headers: { Authorization: `Bearer ${getBearerToken()}` }
    }).then(response => {
      setAirlineData(response?.data?.data?.airline || []);
    }).catch(error => {
      console.error('Error fetching airline data:', error);
    });
  }, []);

  // Function to get airline name by ID
  const getAirlineName = (airlineId) => {
    if (!airlineData || !airlineId) return airlineId; // fallback to ID if no data
    const airline = airlineData.find(item => item.id == airlineId);
    return airline ? airline.full_name : airlineId; // fallback to ID if not found
  };

  const handleAction = (action, packageId) => {
    setConfirmation({ show: true, target: action, packageId });
  };

  const handleConfirm = async () => {
    const { target, packageId } = confirmation;
    setConfirmation({ show: false, target: '', packageId: null });
    
    if (target === 'close') {
      await onClose(packageId);
    } else if (target === 'activate') {
      await onActivate(packageId);
    } else if (target === 'delete') {
      await onDelete(packageId);
    }
  };

  // Get departure and arrival info
  const getDepartureInfo = (pkg) => {
    if (!pkg.departure_flights || pkg.departure_flights.length === 0) return null;
    const firstFlight = pkg.departure_flights[0];
    const lastFlight = pkg.departure_flights[pkg.departure_flights.length - 1];
    return {
      from: firstFlight.departure_airport,
      to: lastFlight.arrival_airport,
      date: firstFlight.departure_date,
      airline: getAirlineName(firstFlight.airline_id)
    };
  };

  const getArrivalInfo = (pkg) => {
    if (!pkg.return_flights || pkg.return_flights.length === 0) return null;
    const firstFlight = pkg.return_flights[0];
    const lastFlight = pkg.return_flights[pkg.return_flights.length - 1];
    return {
      from: firstFlight.departure_airport,
      to: lastFlight.arrival_airport,
      date: firstFlight.departure_date,
      airline: getAirlineName(firstFlight.airline_id)
    };
  };

  return (
    <>
      {packages?.map((pkg, index) => {
        const prices = pkg?.prices || [];
        const priceMap = prices.reduce((acc, p) => { acc[p.room_type] = p.price_adult; return acc; }, {});
        const isLastItem = index === packages.length - 1;
        const departureInfo = getDepartureInfo(pkg);
        const arrivalInfo = getArrivalInfo(pkg);
        
        return (
          <tr key={pkg.id} className="py-3" style={{ borderBottom: isLastItem ? 'none' : '1px solid #e9ecef' }}>
            <td className={'py-3'}>
              <Link to={`/dashboard/view-umrah/${pkg.id}`} className="text-decoration-none bluetext fontweight600">
                {pkg.title}
              </Link>
            </td>
            <td className="py-3">
              {departureInfo && arrivalInfo ? (
                <>
                  {fmtDate(departureInfo.date)}<br />
                  {fmtDate(arrivalInfo.date)}
                </>
              ) : (
                'Dates not available'
              )}
            </td>
            <td className="py-3">
              <div className="d-flex flex-column">
                {departureInfo && (
                  <span className="fontsize12">
                    <i className="fas fa-plane-departure text-info me-1"></i>
                    {departureInfo.from} → {departureInfo.to}
                  </span>
                )}
                {arrivalInfo && (
                  <span className="fontsize12">
                    <i className="fas fa-plane-arrival text-success me-1"></i>
                    {arrivalInfo.from} → {arrivalInfo.to}
                  </span>
                )}
              </div>
            </td>
            <td className="py-3">
              <div className="d-flex flex-column">
                {departureInfo && (
                  <span className="fontsize12">
                    <i className="fas fa-plane text-primary me-1"></i>
                    {departureInfo.airline}
                  </span>
                )}
                {arrivalInfo && (
                  <span className="fontsize12">
                    <i className="fas fa-plane text-primary me-1"></i>
                    {arrivalInfo.airline}
                  </span>
                )}
              </div>
            </td>
            <td className="py-3">
              <div className='d-flex flex-wrap gap-2 py-2'>
                {['double','triple','quad','sharing'].map((rt) => (
                  <Badge key={rt} bg={''} className={'p-2 py-1 RGBAcolor3bg greentext me-1 mb-1'}>{(rt === "sharing" ? "quint/sharing" : rt).toUpperCase()}: {priceMap[rt] ?? '—'}</Badge>
                ))}
              </div>
            </td>
            <td className="py-3">
              <div className="d-flex flex-column">
                <span className="fontweight600 text-primary">
                  {pkg.available_seats || 0} / {pkg.total_seats || 0}
                </span>
                <small className="text-muted">Available / Total</small>
                {pkg.available_seats === 0 && (
                  <Badge bg="danger" className="mt-1">Fully Booked</Badge>
                )}
              </div>
            </td>
            <td className="py-3">
              <Badge className={`py-2 mt-2 ${pkg.status === 'closed' ? 'errorlighterbg redtext' : 'successcolorbg whitetext'}`}>
                {pkg.status === 'closed' ? 'Closed' : 'Active'}
              </Badge>
            </td>
            <td className="py-3">
              <Dropdown align={'end'} autoClose='outside'>
                <Dropdown.Toggle variant={''} id='umrah-actions'>
                  <img className={'img-fluid'} src={verticalicon} alt={'icon'} />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Link to={`../edit-umrah/${pkg.id}`} className={'dropdown-item'}>Edit</Link>
                  <Link to={'#'} className={'dropdown-item'} onClick={() => handleAction('delete', pkg.id)}>Delete</Link>
                  {pkg.status === 'active' ? (
                    <Link to={'#'} className={'dropdown-item'} onClick={() => handleAction('close', pkg.id)}>Close</Link>
                  ) : (
                    <Link to={'#'} className={'dropdown-item'} onClick={() => handleAction('activate', pkg.id)}>Activate</Link>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </td>
          </tr>
        );
      })}
      
      {confirmation.show && (
        <ConfirmationPopup 
          show={confirmation} 
          setShow={setConfirmation} 
          handleConfirm={handleConfirm} 
        />
      )}
    </>
  );
};

export default UmrahTRLoop;